import { useState, useEffect } from 'react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import apiClient from '../../services/api';
import { Transaction, PaginatedResponse } from '../../types';

export function BillingPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [balance, setBalance] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchBalance();
    fetchTransactions();
  }, [page]);

  const fetchBalance = async () => {
    try {
      const response = await apiClient.get<{ balance: number }>('/billing/balance');
      setBalance(response.data.balance);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<PaginatedResponse<Transaction>>(
        `/billing/transactions?page=${page}&page_size=${pageSize}`
      );
      setTransactions(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    const prefix = amount >= 0 ? '+' : '';
    return prefix + '$' + amount.toFixed(4);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('zh-CN');
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      recharge: '充值',
      consumption: '消费',
      adjustment: '调整',
      refund: '退款',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      recharge: 'text-green-600',
      consumption: 'text-red-600',
      adjustment: 'text-blue-600',
      refund: 'text-purple-600',
    };
    return colors[type] || 'text-gray-600';
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">账单充值</h1>
      </div>

      <Card>
        <div className="text-center py-6">
          <p className="text-sm text-gray-600">当前余额</p>
          <p className="text-4xl font-bold text-green-600 mt-2">${balance.toFixed(2)}</p>
          <p className="text-sm text-gray-500 mt-2">请联系管理员进行充值</p>
        </div>
      </Card>

      <Card title="交易记录">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-center text-gray-500 py-8">暂无交易记录</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">类型</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">金额</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">余额</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">描述</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">时间</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(tx.type)}`}>
                          {getTypeLabel(tx.type)}
                        </span>
                      </td>
                      <td className={`py-3 px-4 text-sm text-right font-medium ${getTypeColor(tx.type)}`}>
                        {formatAmount(tx.amount)}
                      </td>
                      <td className="py-3 px-4 text-sm text-right">${tx.balance_after.toFixed(2)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{tx.description || '-'}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatDate(tx.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  共 {total} 条记录
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
